import { DG } from '@cotera/sdk/core';
import {
  DatasetTreeNode,
  ColumnTreeNode,
  TreeNode,
  SidebarTreeStructure,
  NavItem,
  ToolTreeNode,
  ToolTreeStructure,
} from './types';
import { TabManager } from '../../tab-manager';
import { Routes } from '../../routes';
import { IconName } from '@cotera/client/app/components/ui';
import { AnalyticsClient } from '@cotera/client/app/etc';
import { Action } from '../../actions/framework';
import { KeyName } from '../../actions/framework/use-keyboard-shortcuts';

/**
 * Builds a hierarchical tree structure from the flat graph data.
 *
 * This function:
 * 1. Extracts dependencies from each dataset
 * 2. Creates dataset and column nodes
 * 3. Organizes them in a parent-child hierarchy
 * 4. Supports datasets with multiple parents (appearing under each parent)
 */
export const buildDatasetTree = (
  graph: DG.BuildExplorer
): SidebarTreeStructure => {
  const allNodes = new Map<string, TreeNode>();
  const dependencyMap = new Map<string, string[]>();
  const childrenMap = new Map<string, string[]>();

  // Step 1: Create all dataset nodes and extract dependencies
  Object.entries(graph.nodeMap).forEach(([datasetName, node]) => {
    if (node.t !== 'rel') {
      return; // Skip non-relation nodes
    }

    // Get dependencies from the graph result
    const deps: string[] = [];
    const graphResult = graph.graph[datasetName];

    if (graphResult && graphResult.isOk()) {
      // Extract dependency names from the resolved graph
      deps.push(...Object.keys(graphResult.value.deps));
    }

    // Also add path-based dependencies (e.g., "foo/bar" depends on "foo")
    const pathParts = datasetName.split('/');
    if (pathParts.length > 1) {
      const parentPath = pathParts.slice(0, -1).join('/');
      if (!deps.includes(parentPath)) {
        deps.push(parentPath);
      }
    }

    // Create dataset node
    const datasetNode: DatasetTreeNode = {
      id: datasetName,
      type: 'dataset',
      name: datasetName,
      fullPath: datasetName,
      children: [],
      originalNode: node,
      deps,
    };

    allNodes.set(datasetName, datasetNode);
    dependencyMap.set(datasetName, deps);

    // Update children map (reverse dependency lookup)
    deps.forEach((parentName) => {
      if (!childrenMap.has(parentName)) {
        childrenMap.set(parentName, []);
      }
      childrenMap.get(parentName)!.push(datasetName);
    });

    // Step 2: Create column nodes for this dataset
    const columnNodes: ColumnTreeNode[] = [];

    Object.entries(node.cols).forEach(([columnName, columnNode]) => {
      const columnId = `${datasetName}/${columnName}`;

      const columnTreeNode: ColumnTreeNode = {
        id: columnId,
        type: 'column',
        name: columnName,
        fullPath: columnId,
        parentDatasetName: datasetName,
        children: [], // Columns don't have children
        originalNode: columnNode,
      };

      allNodes.set(columnId, columnTreeNode);
      columnNodes.push(columnTreeNode);
    });

    // Sort columns alphabetically and add them to the dataset
    columnNodes.sort((a, b) => a.name.localeCompare(b.name));
    datasetNode.children.push(...columnNodes);
  });

  // Step 3: Build the parent-child relationships for datasets
  allNodes.forEach((node) => {
    if (node.type === 'dataset') {
      const datasetNode = node as DatasetTreeNode;
      const childDatasetNames = childrenMap.get(datasetNode.name) || [];

      // Add child datasets to this dataset's children
      childDatasetNames.forEach((childName) => {
        const childNode = allNodes.get(childName);
        if (childNode !== undefined) {
          // Insert child datasets at the beginning, before columns
          // This puts datasets first, then columns
          const columnNodes = datasetNode.children.filter(
            (child) => child.type === 'column'
          );
          const datasetNodes = datasetNode.children.filter(
            (child) => child.type === 'dataset'
          );

          // Add the new child dataset and sort all datasets alphabetically
          const allDatasetNodes = [...datasetNodes, childNode];
          allDatasetNodes.sort((a, b) => a.name.localeCompare(b.name));

          datasetNode.children = [...allDatasetNodes, ...columnNodes];
        }
      });
    }
  });

  // Step 4: Identify top-level datasets (those with no dependencies)
  const topLevelDatasets: DatasetTreeNode[] = [];

  allNodes.forEach((node) => {
    if (node.type === 'dataset') {
      const datasetNode = node as DatasetTreeNode;
      if (datasetNode.deps.length === 0) {
        topLevelDatasets.push(datasetNode);
      }
    }
  });

  // Sort top-level datasets alphabetically
  topLevelDatasets.sort((a, b) => a.name.localeCompare(b.name));

  return {
    topLevelDatasets,
    allNodes,
    dependencyMap,
    childrenMap,
  };
};

/**
 * Converts a TreeNode to the existing NavItem format for rendering.
 * This allows the tree structure to work with the existing sidebar components.
 */
export const treeNodeToNavItem = (
  node: TreeNode,
  tabManager: TabManager,
  graph: DG.BuildExplorer,
  iconForGraphColumn: (
    graph: DG.BuildExplorer,
    col: { rel: string; name: string }
  ) => string,
  depth: number,
  actions: Record<
    string,
    {
      action: {
        shortcut: KeyName[] | undefined;
        title: string;
        icon: string;
      };
      filter: (item: TreeNode) => boolean;
      execute: (item: TreeNode) => Promise<void>;
      variant?: 'default' | 'destructive' | 'grouped';
    }
  >,
  analytics?: AnalyticsClient
): NavItem => {
  if (node.type === 'dataset') {
    const datasetNode = node as DatasetTreeNode;

    const onClick = () => {
      analytics?.track('Dataset Opened', {
        dataset: datasetNode.name,
        fullPath: datasetNode.fullPath,
      });
      tabManager.push({
        route: Routes.dataset.forId(datasetNode.fullPath).toString(),
        title: datasetNode.name,
        displayTitle:
          depth === 0 ? datasetNode.name : datasetNode.name.split('/').pop()!,
        icon: 'table',
      });
    };

    const isActive = () =>
      tabManager.activeTab ===
      `/dataset/${encodeURIComponent(datasetNode.fullPath)}`;

    const childNavItems = datasetNode.children.map((child) =>
      treeNodeToNavItem(
        child,
        tabManager,
        graph,
        iconForGraphColumn,
        depth + 1,
        actions,
        analytics
      )
    );

    const name = datasetNode.name;

    const quickActions = Object.values(actions)
      .filter((action) => action.filter(node))
      .map((action) => ({
        icon: action.action.icon as IconName,
        label: action.action.title,
        onClick: async () => await action.execute(node),
        variant: action.variant,
      }));

    return {
      t: 'item' as const,
      icon: 'table',
      title: depth === 0 ? name : name.split('/').pop()!,
      onClick,
      isActive,
      quickActions,
      ...(childNavItems.length > 0 && {
        items: childNavItems,
        onExpandChange: (expanded: boolean) => {
          analytics?.track(
            expanded ? 'Dataset Expanded' : 'Dataset Collapsed',
            {
              dataset: datasetNode.name,
              fullPath: datasetNode.fullPath,
            }
          );
        },
      }),
    };
  } else {
    const columnNode = node as ColumnTreeNode;

    const onClick = () => {
      analytics?.track('Column Opened', {
        column: columnNode.name,
        dataset: columnNode.parentDatasetName,
      });

      const columnIcon = iconForGraphColumn(graph, {
        rel: columnNode.parentDatasetName,
        name: columnNode.name,
      });

      tabManager.push({
        route: `/dataset/${encodeURIComponent(
          columnNode.parentDatasetName
        )}/column/${columnNode.name}`,
        title: `${columnNode.parentDatasetName}/${columnNode.name}`,
        displayTitle: columnNode.name,
        icon: columnIcon,
      });
    };

    const isActive = () =>
      tabManager.activeTab ===
      `/dataset/${encodeURIComponent(columnNode.parentDatasetName)}/column/${
        columnNode.name
      }`;

    const icon = iconForGraphColumn(graph, {
      rel: columnNode.parentDatasetName,
      name: columnNode.name,
    });

    const quickActions = Object.values(actions)
      .filter((action) => action.filter(node))
      .map((action) => ({
        icon: action.action.icon as IconName,
        label: action.action.title,
        onClick: async () => await action.execute(node),
        variant: action.variant,
      }));

    return {
      t: 'item' as const,
      title: columnNode.name,
      icon: icon as IconName,
      onClick,
      isActive,
      quickActions,
    };
  }
};

/**
 * Builds a hierarchical tree structure for tools based on "/" path separators.
 *
 * This function:
 * 1. Splits tool names by "/"
 * 2. Creates folder nodes for intermediate paths
 * 3. Organizes them in a parent-child hierarchy
 */
export const buildToolTree = (graph: DG.BuildExplorer): ToolTreeStructure => {
  const allNodes = new Map<string, ToolTreeNode>();
  const childrenMap = new Map<string, string[]>();

  // Step 1: Get all tools from the graph
  const tools: Array<{ name: string; node: DG.Graph.ToolNode }> = [];

  Object.entries(graph.nodeMap).forEach(([symbol, node]) => {
    if (node.t === 'tool') {
      tools.push({ name: symbol, node });
    }
  });

  // Step 2: Create tool nodes and track path-based relationships
  tools.forEach(({ name, node }) => {
    const toolNode: ToolTreeNode = {
      id: name,
      type: 'tool',
      name,
      fullPath: name,
      children: [],
      originalNode: node,
    };

    allNodes.set(name, toolNode);

    // Track parent-child relationships based on path
    const pathParts = name.split('/');
    if (pathParts.length > 1) {
      const parentPath = pathParts.slice(0, -1).join('/');

      if (!childrenMap.has(parentPath)) {
        childrenMap.set(parentPath, []);
      }
      childrenMap.get(parentPath)!.push(name);

      // Create parent folder nodes if they don't exist
      let currentPath = '';
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (!part) {
          continue; // Skip empty parts
        }

        const previousPath = currentPath;
        currentPath = currentPath ? `${currentPath}/${part}` : part;

        if (!allNodes.has(currentPath)) {
          // Create a synthetic folder node
          const folderNode: ToolTreeNode = {
            id: currentPath,
            type: 'tool',
            name: currentPath,
            fullPath: currentPath,
            children: [],
            originalNode: node, // Use the first tool's node as placeholder
          };

          allNodes.set(currentPath, folderNode);
        }

        // Track parent-child relationship
        if (previousPath) {
          if (!childrenMap.has(previousPath)) {
            childrenMap.set(previousPath, []);
          }
          if (!childrenMap.get(previousPath)!.includes(currentPath)) {
            childrenMap.get(previousPath)!.push(currentPath);
          }
        }
      }
    }
  });

  // Step 3: Build parent-child relationships
  allNodes.forEach((node) => {
    const childNames = childrenMap.get(node.name) || [];

    childNames.forEach((childName) => {
      const childNode = allNodes.get(childName);
      if (childNode !== undefined) {
        node.children.push(childNode);
      }
    });

    // Sort children alphabetically
    node.children.sort((a, b) => a.name.localeCompare(b.name));
  });

  // Step 4: Identify top-level tools (those without "/" or only one level)
  const topLevelTools: ToolTreeNode[] = [];

  allNodes.forEach((node) => {
    const pathParts = node.name.split('/');
    if (pathParts.length === 1) {
      topLevelTools.push(node);
    }
  });

  // Sort top-level tools alphabetically
  topLevelTools.sort((a, b) => a.name.localeCompare(b.name));

  return {
    topLevelTools,
    allNodes,
  };
};

/**
 * Converts a ToolTreeNode to NavItem format for rendering.
 */
export const toolTreeNodeToNavItem = (
  node: ToolTreeNode,
  tabManager: TabManager,
  graph: DG.BuildExplorer,
  depth: number,
  actions: Record<
    string,
    {
      action: Action<any, any>;
      filter: (node: ToolTreeNode) => boolean;
      execute: (node: ToolTreeNode) => Promise<void>;
      variant?: 'default' | 'destructive' | 'grouped';
    }
  >,
  analytics?: AnalyticsClient
): NavItem => {
  // Check if this is a real tool (exists in graph) or just a folder (synthetic)
  const isRealTool = graph.nodeMap[node.fullPath]?.t === 'tool';

  const onClick = () => {
    // Only track and open if it's a real tool
    if (isRealTool) {
      analytics?.track('Tool Opened', {
        tool: node.name,
        fullPath: node.fullPath,
      });
      tabManager.push({
        route: Routes.tool.forId(node.fullPath).toString(),
        title: node.name,
        displayTitle: depth === 0 ? node.name : node.name.split('/').pop()!,
        icon: 'wrench-screwdriver',
      });
    }
  };

  const isActive = () =>
    isRealTool
      ? tabManager.activeTab === `/tool/${encodeURIComponent(node.fullPath)}`
      : false;

  const childNavItems = node.children.map((child) =>
    toolTreeNodeToNavItem(
      child,
      tabManager,
      graph,
      depth + 1,
      actions,
      analytics
    )
  );

  // Only show quick actions for real tools (leaf nodes), not synthetic folders
  const quickActions = isRealTool
    ? Object.values(actions)
        .filter((action) => action.filter(node))
        .map((action) => ({
          icon: action.action.icon as IconName,
          label: action.action.title,
          onClick: async () => await action.execute(node),
          variant: action.variant,
        }))
    : [];

  const displayName = depth === 0 ? node.name : node.name.split('/').pop()!;

  return {
    t: 'item' as const,
    icon: 'wrench-screwdriver',
    title: displayName,
    onClick,
    isActive,
    ...(quickActions.length > 0 && { quickActions }),
    ...(childNavItems.length > 0 && {
      items: childNavItems,
      onExpandChange: (expanded: boolean) => {
        analytics?.track(expanded ? 'Tool Expanded' : 'Tool Collapsed', {
          tool: node.name,
          fullPath: node.fullPath,
        });
      },
    }),
  };
};
